import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Clock, FileText, CheckCircle, AlertCircle, ArrowLeft, ArrowRight } from 'lucide-react';
import { Policy } from '@/types/policy';
import { PolicyReaderProps, ReadingMetrics, PageMetrics, DocumentFormat, PolicyReadingSession } from '@/types/policy-reader';
import { getPolicy } from '@/services/policyService';
import { useAuth } from '@/contexts/AuthContext';
import { useTenancy } from '@/contexts/TenancyContext';
import { PDFViewer } from './PDFViewer';
import { DOCXViewer } from './DOCXViewer';
import { PolicyQuizModal } from './PolicyQuizModal';
import { formatDateTime, formatDuration } from '@/utils/time-utils';
import { downloadFromSDK, previewDocument } from '../../js/external-js/variableModules/downloadFromSDK.module';
import { firebaseConfig, storageRef } from '../../js/external-js/config/main_config';
import { getDocument } from '../../services/documentsService';
import { getCurrentScreenForPolicy, setCurentScreenForPolicy } from '@/app/pages/policy-reader/components/service';

// Wrapper components that handle file downloading
interface ViewerWithDownloadProps {
  filePaths: string[];
  fileName: string;
  currentPage: number;
  onPageChange: (page: number) => void;
  onTotalPagesChange: (total: number) => void;
}

// Enhanced PDF viewer that handles both regular policies and document library policies
const PDFViewerWithDownload: React.FC<ViewerWithDownloadProps & { policy?: any }> = ({
  filePaths,
  fileName,
  currentPage,
  onPageChange,
  onTotalPagesChange,
  policy
}) => {
  const [fileUrl, setFileUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloadAttempted, setDownloadAttempted] = useState(false);
  const { activeTenancy } = useTenancy();

  useEffect(() => {
    const downloadFile = async () => {
      try {
        setLoading(true);
        setError(null);

        let url: string | null = null;
        let lastError: Error | null = null;

        // Check if this is a document library policy
        const isDocumentLibraryPolicy = policy?.policy_metadata?.policyType === 'document';

        if (isDocumentLibraryPolicy && policy?.policy_id) {
          // Handle document library policies by constructing storage path directly
          try {
            console.log('Loading document library policy:', policy.policy_id);
            const companyId = activeTenancy.id || window.activeTenancy?.id;
            const docId = policy.policy_id;
            const storagePath = `customers/${companyId}/keyDocuments/otherDocs/uploaded/${docId}/${fileName}`;

            console.log('Constructed storage path for PDF:', storagePath);

            // Use downloadFromSDK directly with the constructed path
            url = await downloadFromSDK(
              firebaseConfig,
              storageRef,
              'pdf',
              null,
              fileName,
              storagePath,
              false
            );

            if (url) {
              console.log('Successfully downloaded document library PDF file');
            }
          } catch (err) {
            console.warn('Failed to load document library policy:', err);
            lastError = err instanceof Error ? err : new Error('Failed to load document library policy');
          }
        }

        // If document library approach failed or this is a regular policy, try file paths
        if (!url && filePaths.length > 0) {
          const maxAttempts = Math.min(filePaths.length, 3);
          for (let i = 0; i < maxAttempts; i++) {
            const filePath = filePaths[i];
            if (!filePath) continue;

            try {
              url = await downloadFromSDK(
                firebaseConfig,
                storageRef,
                'pdf',
                null,
                fileName,
                filePath,
                false
              );

              if (url) {
                break;
              }
            } catch (err) {
              lastError = err instanceof Error ? err : new Error(`Failed to download from ${filePath}`);
              continue;
            }
          }
        }

        if (!url) {
          throw lastError || new Error('Failed to download PDF file from all available sources');
        }

        setFileUrl(url);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to download PDF');
      } finally {
        setLoading(false);
        setDownloadAttempted(true);
      }
    };

    // Only download if we haven't attempted yet
    if (!downloadAttempted && !fileUrl) {
      downloadFile();
    }
  }, [filePaths, fileName, downloadAttempted, fileUrl, policy]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2" />
          <p className="text-muted-foreground">Loading PDF...</p>
        </div>
      </div>
    );
  }

  if (error || !fileUrl) {
    return (
      <div className="flex items-center justify-center h-64 bg-muted rounded-lg">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto mb-2" />
          <p className="text-muted-foreground">{error || 'Failed to load PDF'}</p>
        </div>
      </div>
    );
  }

  return (
    <PDFViewer
      fileUrl={fileUrl}
      currentPage={currentPage}
      onPageChange={onPageChange}
      onTotalPagesChange={onTotalPagesChange}
    />
  );
};

const DOCXViewerWithDownload: React.FC<ViewerWithDownloadProps & { policy?: any }> = ({
  filePaths,
  fileName,
  currentPage,
  onPageChange,
  onTotalPagesChange,
  policy
}) => {
  const [fileUrl, setFileUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloadAttempted, setDownloadAttempted] = useState(false);
  const { activeTenancy } = useTenancy();

  useEffect(() => {
    const downloadFile = async () => {
      try {
        setLoading(true);
        setError(null);

        let url: string | null = null;
        let lastError: Error | null = null;

        // Check if this is a document library policy
        const isDocumentLibraryPolicy = policy?.policy_metadata?.policyType === 'document';

        if (isDocumentLibraryPolicy && policy?.policy_id) {
          // Handle document library policies by constructing storage path directly
          try {
            console.log('Loading document library policy:', policy.cpolicy_idomponentID);
            const companyId = activeTenancy.id || window.activeTenancy?.id;
            const docId = policy.policy_id;
            const storagePath = `customers/${companyId}/keyDocuments/otherDocs/uploaded/${docId}/${fileName}`;

            console.log('Constructed storage path for DOCX:', storagePath);

            // Use downloadFromSDK directly with the constructed path
            url = await downloadFromSDK(
              firebaseConfig,
              storageRef,
              'display', // This returns a blob URL for other file types
              null,
              fileName,
              storagePath,
              false
            );

            if (url) {
              console.log('Successfully downloaded document library DOCX file');
            }
          } catch (err) {
            console.warn('Failed to load document library policy:', err);
            lastError = err instanceof Error ? err : new Error('Failed to load document library policy');
          }
        }

        // If document library approach failed or this is a regular policy, try file paths
        if (!url && filePaths.length > 0) {
          const maxAttempts = Math.min(filePaths.length, 3);
          for (let i = 0; i < maxAttempts; i++) {
            const filePath = filePaths[i];
            if (!filePath) continue;

            try {
              url = await downloadFromSDK(
                firebaseConfig,
                storageRef,
                'display', // This returns a blob URL for other file types
                null,
                fileName,
                filePath,
                false
              );

              if (url) {
                break;
              }
            } catch (err) {
              lastError = err instanceof Error ? err : new Error(`Failed to download from ${filePath}`);
              continue;
            }
          }
        }

        if (!url) {
          throw lastError || new Error('Failed to download DOCX file from all available sources');
        }

        setFileUrl(url);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to download DOCX');
      } finally {
        setLoading(false);
        setDownloadAttempted(true);
      }
    };

    // Only download if we haven't attempted yet
    if (!downloadAttempted && !fileUrl) {
      downloadFile();
    }
  }, [filePaths, fileName, downloadAttempted, fileUrl, policy]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2" />
          <p className="text-muted-foreground">Loading DOCX...</p>
        </div>
      </div>
    );
  }

  if (error || !fileUrl) {
    return (
      <div className="flex items-center justify-center h-64 bg-muted rounded-lg">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto mb-2" />
          <p className="text-muted-foreground">{error || 'Failed to load DOCX'}</p>
        </div>
      </div>
    );
  }

  return (
    <DOCXViewer
      fileUrl={fileUrl}
      currentPage={currentPage}
      onPageChange={onPageChange}
      onTotalPagesChange={onTotalPagesChange}
    />
  );
};

export const PolicyReader: React.FC<PolicyReaderProps> = ({
  policyId,
  policy: policyProp,
  onComplete,
  onQuizComplete
}) => {
  const { user } = useAuth();
  const { activeTenancy } = useTenancy();

  // State management
  const [policy, setPolicy] = useState<Policy | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [documentFormat, setDocumentFormat] = useState<DocumentFormat>('unknown');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [showQuiz, setShowQuiz] = useState(false);
  const [readingComplete, setReadingComplete] = useState(false);
  const [quizCompleted, setQuizCompleted] = useState(false);

  // Reading metrics
  const [metrics, setMetrics] = useState<ReadingMetrics>({
    totalReadTime: 0,
    totalDwellTime: 0,
    pageMetrics: [],
    startTime: new Date(),
    completed: false
  });

  // Refs for tracking
  const startTimeRef = useRef<Date>(new Date());
  const pageStartTimeRef = useRef<Date>(new Date());
  const dwellTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isActiveRef = useRef(true);

  // Load policy data
  useEffect(() => {
    const loadPolicy = async () => {
      try {
        setLoading(true);

        // Use passed policy prop if available, otherwise load from API
        let policyData = policyProp;
        if (!policyData) {
          policyData = await getPolicy(policyId);
          if (!policyData) {
            throw new Error('Policy not found');
          }
        }
        console.log('Loaded policy:', policyData);

        setPolicy(policyData);

        // Determine document format from file name
        const fileName = policyData.file_name || policyData.policy_metadata?.file_name || policyData.name || '';
        const extension = fileName.split('.').pop()?.toLowerCase();

        if (extension === 'pdf') {
          setDocumentFormat('pdf');
        } else if (extension === 'docx' || extension === 'doc') {
          setDocumentFormat('docx');
        } else {
          setDocumentFormat('unknown');
        }

      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load policy');
      } finally {
        setLoading(false);
      }
    };

    loadPolicy();
  }, [policyId, policyProp]);

  // Initialize page metrics when total pages is known
  useEffect(() => {
    if (totalPages > 0) {
      const initialPageMetrics: PageMetrics[] = Array.from({ length: totalPages }, (_, i) => ({
        pageNumber: i + 1,
        readTime: 0,
        dwellTime: 0,
        viewCount: 0
      }));
      getCurrentScreenForPolicy(user, policyId).then((currentScreen) => {
        if (currentScreen >= totalPages) {
          setReadingComplete(true);
        }

        let qCompleted = false;
        console.log('asd', currentScreen, totalPages, quizCompleted);

        // Current pages will exceed the number in the DB if completed
        if (currentScreen > totalPages) {
          qCompleted = true;
          setQuizCompleted(true);
          // handlePageChange(totalPages)
        } else {
          handlePageChange(currentScreen);
        }
        console.log('asd', currentScreen, totalPages, quizCompleted);


        // setMetrics(prev => ({
        //   ...prev,
        //   pageMetrics: initialPageMetrics
        // }));

        setMetrics(prev => ({
          ...prev,
          pageMetrics: prev.pageMetrics.map(pm =>
            pm.pageNumber <= currentPage && pm.readTime === 0
              ? { ...pm, readTime: 1 }
              : pm
          )
        }));
      });

      // setMetrics(prev => ({
      //   ...prev,
      //   pageMetrics: initialPageMetrics
      // }));
    }
  }, [totalPages, quizCompleted]);

  // Track page view time
  useEffect(() => {
    const now = new Date();
    pageStartTimeRef.current = now;

    // Update page metrics for current page
    setMetrics(prev => {
      const newPageMetrics = [...prev.pageMetrics];
      if (newPageMetrics[currentPage - 1]) {
        const pageMetric = newPageMetrics[currentPage - 1];
        pageMetric.viewCount += 1;
        if (!pageMetric.firstViewed) {
          pageMetric.firstViewed = now;
        }
        pageMetric.lastViewed = now;
      }
      return {
        ...prev,
        pageMetrics: newPageMetrics
      };
    });

    return () => {
      // Calculate time spent on this page when leaving
      const timeSpent = Date.now() - pageStartTimeRef.current.getTime();
      setMetrics(prev => {
        const newPageMetrics = [...prev.pageMetrics];
        if (newPageMetrics[currentPage - 1]) {
          newPageMetrics[currentPage - 1].readTime += timeSpent;
        }
        return {
          ...prev,
          totalReadTime: prev.totalReadTime + timeSpent,
          pageMetrics: newPageMetrics
        };
      });
    };
  }, [currentPage]);

  // Track dwell time (time spent actively viewing)
  useEffect(() => {
    const startDwellTracking = () => {
      if (dwellTimerRef.current) {
        clearInterval(dwellTimerRef.current);
      }

      dwellTimerRef.current = setInterval(() => {
        if (isActiveRef.current) {
          setMetrics(prev => {
            const newPageMetrics = [...prev.pageMetrics];
            if (newPageMetrics[currentPage - 1]) {
              newPageMetrics[currentPage - 1].dwellTime += 1000; // 1 second
            }
            return {
              ...prev,
              totalDwellTime: prev.totalDwellTime + 1000,
              pageMetrics: newPageMetrics
            };
          });
        }
      }, 1000);
    };

    const handleVisibilityChange = () => {
      isActiveRef.current = !document.hidden;
    };

    const handleFocus = () => {
      isActiveRef.current = true;
    };

    const handleBlur = () => {
      isActiveRef.current = false;
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    window.addEventListener('blur', handleBlur);

    startDwellTracking();

    return () => {
      if (dwellTimerRef.current) {
        clearInterval(dwellTimerRef.current);
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('blur', handleBlur);
    };
  }, [currentPage]);

  // Handle page navigation
  const handlePageChange = useCallback((page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      console.log('asdd', quizCompleted)
      if (!quizCompleted) setCurentScreenForPolicy(user, policyId, page);
    }

  }, [totalPages, quizCompleted]);

  // Handle document completion
  const handleDocumentComplete = useCallback(() => {
    const now = new Date();
    const updatedMetrics: ReadingMetrics = {
      ...metrics,
      endTime: now,
      completed: true
    };

    setMetrics(updatedMetrics);
    setReadingComplete(true);
    setShowQuiz(true);


    // Create reading session
    const session: PolicyReadingSession = {
      id: `session_${Date.now()}`,
      policyId,
      userId: user?.uid || '',
      tenantId: activeTenancy?.id || '',
      startTime: startTimeRef.current,
      endTime: now,
      metrics: updatedMetrics,
      completed: true
    };

    onComplete?.(session);
  }, [metrics, policyId, user?.uid, activeTenancy?.id, onComplete]);

  // Check if user has read all pages
  useEffect(() => {
    if (metrics.pageMetrics.length > 0) {
      const allPagesViewed = metrics.pageMetrics.every(page => page.viewCount > 0);
      if (allPagesViewed && !readingComplete) {
        handleDocumentComplete();
      }
    }
  }, [metrics.pageMetrics, readingComplete, handleDocumentComplete]);

  // Memoize file paths to prevent unnecessary re-downloads
  const filePaths = useMemo(() => {
    if (!policy) return [];
    return [
      policy.filePath,
      policy.fileUploadPath,
      policy.policy_metadata?.file_url
    ].filter(Boolean); // Remove null/undefined values
  }, [policy?.filePath, policy?.fileUploadPath, policy?.policy_metadata?.file_url]);

  const renderDocumentViewer = () => {
    if (!policy) return null;

    // Check if this is a document library policy or if we have file paths
    const isDocumentLibraryPolicy = policy?.policy_metadata?.policyType === 'document';
    const hasFilePaths = filePaths.length > 0;

    if (!isDocumentLibraryPolicy && !hasFilePaths) {
      return (
        <div className="flex items-center justify-center h-64 bg-muted rounded-lg">
          <div className="text-center">
            <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto mb-2" />
            <p className="text-muted-foreground">No file paths found</p>
          </div>
        </div>
      );
    }

    switch (documentFormat) {
      case 'pdf':
        return (
          <PDFViewerWithDownload
            filePaths={filePaths}
            fileName={policy.file_name || policy.policy_metadata?.file_name || (policy as any).name || ''}
            currentPage={currentPage}
            onPageChange={handlePageChange}
            onTotalPagesChange={setTotalPages}
            policy={policy}
          />
        );
      case 'docx':
        return (
          <DOCXViewerWithDownload
            filePaths={filePaths}
            fileName={policy.file_name || policy.policy_metadata?.file_name || (policy as any).name || ''}
            currentPage={currentPage}
            onPageChange={handlePageChange}
            onTotalPagesChange={setTotalPages}
            policy={policy}
          />
        );
      default:
        return (
          <div className="flex items-center justify-center h-64 bg-muted rounded-lg">
            <div className="text-center">
              <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto mb-2" />
              <p className="text-muted-foreground">Unsupported document format</p>
            </div>
          </div>
        );
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2" />
          <p className="text-muted-foreground">Loading policy...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <Card className="border-destructive">
        <CardContent className="pt-6">
          <div className="flex items-center space-x-2 text-destructive">
            <AlertCircle className="h-5 w-5" />
            <p>{error}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!policy) {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className="text-muted-foreground">Policy not found</p>
        </CardContent>
      </Card>
    );
  }

  const readingProgress = readingComplete
    ? 100
    : totalPages > 0
      ? (metrics.pageMetrics.filter(
      p => (p.dwellTime || p.readTime || p.viewCount) > 0
    ).length / totalPages) * 100
      : 0;
  const t = totalPages > 0 ? (metrics.pageMetrics.filter(p => (p.dwellTime || p.readTime || p.viewCount) > 0).length / totalPages) * 100 : 0;

  return (
    <div className="space-y-4">

      {/* Document Viewer */}
      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-start justify-between">
            <div className="space-y-1">
              <CardTitle className="text-lg">{policy.policy_name}</CardTitle>
              {/*<div className="flex items-center space-x-2 text-sm text-muted-foreground">*/}
              {/*  <FileText className="h-4 w-4" />*/}
              {/*  <span>Version {policy.policy_version}</span>*/}
              {/*  <Badge variant="outline">{policy.policy_status}</Badge>*/}
              {/*  {policy.policyCategory && (*/}
              {/*    <Badge variant="secondary">{policy.policyCategory}</Badge>*/}
              {/*  )}*/}
              {/*</div>*/}
              {/*{policy.policy_history?.last_reviewed && (*/}
              {/*  <div className="flex items-center space-x-1 text-sm text-muted-foreground">*/}
              {/*    <Clock className="h-4 w-4" />*/}
              {/*    <span>Last reviewed: {formatDateTime(policy.policy_history.last_reviewed)}</span>*/}
              {/*  </div>*/}
              {/*)}*/}
            </div>
            <div className="text-right space-y-1">
              {readingComplete && (
                <div>
                  <Badge variant="default" className="bg-green-600 hover:bg-green-700 transition-colors duration-200 shadow-sm">
                    <CheckCircle className="h-3 w-3 mr-1" />
                    Reading Completed
                  </Badge>
                </div>
              )}
            </div>
          </div>
        </CardHeader>
        {/*<CardHeader className="pt-0">*/}
        {/*  <div className="space-y-2">*/}
        {/*    <div className="flex justify-between text-sm">*/}
        {/*      <span>Reading Progress</span>*/}
        {/*      <span>{Math.round(readingProgress)}%</span>*/}
        {/*    </div>*/}
        {/*    <Progress value={readingProgress} className="h-2" />*/}
        {/*  </div>*/}
        {/*</CardHeader>*/}

        {/* Navigation */}
        {totalPages > 1 && (
          <CardContent>
            <div className="flex items-center justify-between">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage <= 1}
              >
                <ArrowLeft className="h-4 w-4 mr-1" />
                Previous
              </Button>

              {/*<div className="flex items-center space-x-2">*/}
              {/*  <span className="text-sm text-muted-foreground">*/}
              {/*    Page {currentPage} of {totalPages}*/}
              {/*  </span>*/}
              {/*</div>*/}

              <div className="flex items-center mt-0">
                {currentPage < totalPages && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(currentPage + 1)}
                  >
                    Next
                    <ArrowRight className="h-4 w-4 ml-1" />
                  </Button>
                )}
                {currentPage >= totalPages && !quizCompleted && (
                  <Button
                    variant="default"
                    size="sm"
                    onClick={handleDocumentComplete}
                    className="bg-green-600 hover:bg-green-700 transition-colors duration-200 shadow-sm"
                  >
                  <CheckCircle className="h-4 w-4 mr-1.5" />
                    Finish
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        )}
        <CardContent className="p-0">
          {renderDocumentViewer()}
        </CardContent>


      </Card>

      {/* Quiz Modal */}
      {showQuiz && (
        <PolicyQuizModal
          policyId={policyId}
          isOpen={showQuiz}
          onClose={(passed: boolean): void => {
            console.log('passed', passed);
            setShowQuiz(false)
            setQuizCompleted(passed)

            if (!passed) return;
            setCurentScreenForPolicy(user, policyId, totalPages + 1);
          }}
          onComplete={onQuizComplete}
        />
      )}
    </div>
  );
};
